
import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import Modal from '../../../components/base/Modal';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface ManualPayment {
  id: string;
  date: string;
  type: 'income' | 'expense';
  amount: number;
  description: string;
  category: string;
  notes?: string;
}

interface ManualPaymentsFormProps {
  onSave: () => void;
}

const paymentTypes = [
  { value: 'income', label: 'Einnahme' },
  { value: 'expense', label: 'Ausgabe' }
];

const categories = [
  // Einnahmen-Kategorien
  'Zusätzliche Verkäufe',
  'Provisionen',
  'Erstattungen',
  'Zinserträge',
  'Sonstige Einnahmen',
  // Ausgaben-Kategorien
  'Reparaturen',
  'Transport',
  'Büromaterial',
  'Rechtsberatung',
  'Versicherungszahlungen',
  'Bankgebühren',
  'Steuerzahlungen',
  'Sonstige Ausgaben'
];

function ManualPaymentsForm({ onSave }: ManualPaymentsFormProps) {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingPayment, setEditingPayment] = useState<ManualPayment | null>(null);
  const [existingPayments, setExistingPayments] = useState<ManualPayment[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form states - KORRIGIERT: Richtige Feldnamen
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'expense' as 'income' | 'expense',
    amount: '',
    description: '',
    category: '',
    notes: ''
  });

  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    if (user) {
      loadExistingPayments();
    }
  }, [user]);

  const loadExistingPayments = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'manual_payment')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const paymentsList = (data || []).map(item => ({
        id: item.id,
        ...item.data
      }));

      setExistingPayments(paymentsList);
    } catch (error) {
      console.error('Error loading manual payments:', error);
      setExistingPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      type: 'expense',
      amount: '',
      description: '',
      category: '',
      notes: ''
    });
    setEditingPayment(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount || !user) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setIsSaving(true);

    try {
      const paymentData = {
        date: formData.date,
        type: formData.type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        category: formData.category || (formData.type === 'income' ? 'Sonstige Einnahmen' : 'Sonstige Ausgaben'),
        notes: formData.notes
      };

      if (editingPayment) {
        // Update existing payment
        const { error } = await supabase
          .from('daten')
          .update({
            data: paymentData
          })
          .eq('id', editingPayment.id)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Create new payment
        const { error } = await supabase
          .from('daten')
          .insert({
            user_id: user.id,
            data_type: 'manual_payment',
            data: paymentData
          });

        if (error) throw error;
      }

      await loadExistingPayments();
      onSave(); // Reload data in parent component
      setIsModalOpen(false);
      resetForm();
      alert('✅ Zahlung wurde erfolgreich gespeichert!');
      
    } catch (error) {
      console.error('Error saving manual payment:', error);
      alert('❌ Fehler beim Speichern der Zahlung. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (payment: ManualPayment) => {
    setEditingPayment(payment);
    setFormData({
      date: payment.date,
      type: payment.type,
      amount: payment.amount.toString(),
      description: payment.description,
      category: payment.category,
      notes: payment.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (paymentId: string) => {
    if (!window.confirm('Möchten Sie diese Zahlung wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', paymentId)
        .eq('user_id', user?.id);

      if (error) throw error;

      await loadExistingPayments();
      onSave(); // Reload data in parent component
      alert('✅ Zahlung wurde gelöscht!');
      
    } catch (error) {
      console.error('Error deleting manual payment:', error);
      alert('❌ Fehler beim Löschen der Zahlung.');
    }
  };

  // Filter payments by selected month
  const filteredPayments = existingPayments.filter(payment => {
    const paymentDate = new Date(payment.date);
    const year = paymentDate.getFullYear();
    const month = paymentDate.getMonth() + 1;
    const paymentMonth = `${year}-${String(month).padStart(2, '0')}`;
    return paymentMonth === filterMonth;
  });

  // Calculate monthly statistics
  const monthlyIncome = filteredPayments
    .filter(p => p.type === 'income')
    .reduce((sum, p) => sum + p.amount, 0);
  
  const monthlyExpenses = filteredPayments
    .filter(p => p.type === 'expense')
    .reduce((sum, p) => sum + p.amount, 0);
  
  const monthlyNet = monthlyIncome - monthlyExpenses;

  // Group payments by category
  const groupedPayments = filteredPayments.reduce((groups, payment) => {
    const category = payment.category || (payment.type === 'income' ? 'Sonstige Einnahmen' : 'Sonstige Ausgaben');
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(payment);
    return groups;
  }, {} as Record<string, ManualPayment[]>);

  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount)) {
      return 'CHF 0.00';
    }
    return `CHF ${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade manuelle Zahlungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Header mit Statistiken */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 sm:p-6">
        <div className="flex flex-col space-y-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 flex items-center">
                <i className="ri-wallet-line mr-2"></i>
                Manuelle Zahlungen
              </h3>
              <div className="flex flex-col space-y-2">
                <label className="text-xs sm:text-sm font-medium text-gray-700">
                  Monat auswählen:
                </label>
                <input
                  type="month"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap text-sm font-medium flex items-center justify-center mt-3 sm:mt-0"
            >
              <i className="ri-add-line mr-2"></i>
              Neue Zahlung
            </button>
          </div>
        </div>

        {/* Monatliche Statistiken */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Einnahmen</h4>
              <i className="ri-arrow-up-line text-green-600 text-base"></i>
            </div>
            <p className="text-lg sm:text-xl font-bold text-green-600">
              {formatCurrency(monthlyIncome)}
            </p>
            <p className="text-xs text-gray-500">
              {filteredPayments.filter(p => p.type === 'income').length} Zahlungen
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Ausgaben</h4>
              <i className="ri-arrow-down-line text-red-600 text-base"></i>
            </div>
            <p className="text-lg sm:text-xl font-bold text-red-600">
              {formatCurrency(monthlyExpenses)}
            </p>
            <p className="text-xs text-gray-500">
              {filteredPayments.filter(p => p.type === 'expense').length} Zahlungen
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Netto</h4>
              <i className={`ri-calculator-line text-base ${monthlyNet >= 0 ? 'text-green-600' : 'text-red-600'}`}></i>
            </div>
            <p className={`text-lg sm:text-xl font-bold ${monthlyNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {monthlyNet >= 0 ? '+' : ''}{formatCurrency(monthlyNet)}
            </p>
            <p className="text-xs text-gray-500">
              {filteredPayments.length} Zahlungen gesamt
            </p>
          </div>
        </div>
      </div>

      {/* Zahlungsliste */}
      {filteredPayments.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="text-gray-400 mb-4">
            <i className="ri-wallet-line text-4xl sm:text-6xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto"></i>
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">
            Keine manuellen Zahlungen vorhanden
          </h3>
          <p className="text-gray-500 text-sm sm:text-base">
            Erfassen Sie Ihre erste manuelle Zahlung für den Monat {new Date(filterMonth + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}.
          </p>
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-list-check mr-2"></i>
              Zahlungen für {new Date(filterMonth + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })} ({filteredPayments.length})
            </h3>
          </div>

          {/* Mobile: Karten-Ansicht */}
          <div className="block sm:hidden divide-y divide-gray-200">
            {Object.entries(groupedPayments).map(([category, payments]) => (
              <div key={category} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 text-sm">{category}</h4>
                  <span className="text-xs text-gray-500">
                    {payments.length} {payments.length === 1 ? 'Zahlung' : 'Zahlungen'}
                  </span>
                </div>
                <div className="space-y-3">
                  {payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((payment) => (
                    <div key={payment.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              payment.type === 'income' ? 'bg-green-500' : 'bg-red-500'
                            }`}></div>
                            <p className="font-medium text-gray-900 text-sm truncate">{payment.description}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-gray-500">
                              {new Date(payment.date).toLocaleDateString('de-DE')}
                            </p>
                            <p className={`text-sm font-bold ${
                              payment.type === 'income' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {payment.type === 'income' ? '+' : '-'} {formatCurrency(payment.amount)}
                            </p>
                            {payment.notes && (
                              <p className="text-xs text-gray-600 mt-1">{payment.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-1 ml-3 flex-shrink-0">
                          <button
                            onClick={() => handleEdit(payment)}
                            className="p-2 text-gray-400 hover:text-blue-600 cursor-pointer rounded"
                            title="Bearbeiten"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(payment.id)}
                            className="p-2 text-gray-400 hover:text-red-600 cursor-pointer rounded"
                            title="Löschen"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Tabellen-Ansicht */}
          <div className="hidden sm:block">
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                <div className="col-span-2">Datum</div>
                <div className="col-span-1">Typ</div>
                <div className="col-span-3">Beschreibung</div>
                <div className="col-span-2">Kategorie</div>
                <div className="col-span-2">Betrag</div>
                <div className="col-span-2">Aktionen</div>
              </div>
            </div>
            
            <div className="divide-y divide-gray-200">
              {filteredPayments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((payment) => (
                <div key={payment.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-2">
                      <span className="text-sm text-gray-900">
                        {new Date(payment.date).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        payment.type === 'income' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {payment.type === 'income' ? 'Einnahme' : 'Ausgabe'}
                      </span>
                    </div>
                    <div className="col-span-3">
                      <div>
                        <p className="font-medium text-gray-900 truncate">{payment.description}</p>
                        {payment.notes && (
                          <p className="text-xs text-gray-500 mt-1 truncate">{payment.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-900 truncate">{payment.category}</span>
                    </div>
                    <div className="col-span-2">
                      <span className={`text-sm font-bold ${
                        payment.type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {payment.type === 'income' ? '+' : '-'} {formatCurrency(payment.amount)}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(payment)}
                          className="p-1 text-gray-400 hover:text-blue-600 cursor-pointer"
                          title="Bearbeiten"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="p-1 text-gray-400 hover:text-red-600 cursor-pointer"
                          title="Löschen"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal für neue/bearbeitete Zahlung */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingPayment ? 'Zahlung bearbeiten' : 'Neue manuelle Zahlung'}
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Datum"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              required
            />
            
            <Select
              label="Typ"
              options={paymentTypes}
              value={formData.type}
              onChange={(value) => setFormData(prev => ({ ...prev, type: value as 'income' | 'expense' }))}
              required
            />
          </div>
          
          <Input
            label="Beschreibung"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Kurze Beschreibung der Zahlung..."
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Betrag (CHF)"
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
              required
            />
            
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={formData.category}
              onChange={(value) => setFormData(prev => ({ ...prev, category: value as string }))}
              placeholder="Kategorie auswählen..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notizen (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={3}
              placeholder="Zusätzliche Informationen..."
            />
          </div>
          
          {/* Vorschau */}
          {formData.amount && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Vorschau:</h4>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                <span className="text-sm text-gray-600">
                  {formData.type === 'income' ? 'Einnahme' : 'Ausgabe'}: {formData.description}
                </span>
                <span className={`font-bold text-lg ${
                  formData.type === 'income' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formData.type === 'income' ? '+' : '-'} CHF {parseFloat(formData.amount || '0').toFixed(2)}
                </span>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={resetForm}
              className="w-full sm:flex-1"
            >
              Zurücksetzen
            </Button>
            <Button
              type="submit"
              disabled={isSaving || !formData.description.trim() || !formData.amount}
              className="w-full sm:flex-1"
            >
              {isSaving ? (
                <>
                  <i className="ri-loader-line mr-2 animate-spin"></i>
                  Speichere...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  {editingPayment ? 'Aktualisieren' : 'Zahlung hinzufügen'}
                </>
              )}
            </Button>
          </div>
          
        </form>
      </Modal>
    </div>
  );
}

export default ManualPaymentsForm;
